import { useEffect, useState } from 'react';

/**
 * QA-577 — 탭을 오래 열어 둔 운영자가 예전 백오피스 코드로 계속 조회하는 문제. 배포마다 진입 번들 이름
 * (`/assets/index-<hash>.js`)이 바뀌므로, 지금 페이지가 불러온 번들과 서버의 최신 index.html 번들을 비교한다.
 * 다르면 새 버전이 배포된 것 — 화면에 새로고침 안내를 띄운다. 개발 서버(번들 이름 없음)에서는 하지 않는다.
 */
const BUNDLE_RE = /\/assets\/index-[\w-]+\.js/;
const CHECK_INTERVAL_MS = 5 * 60_000;

/** 지금 페이지가 불러온 진입 번들 경로. 없으면(개발 서버) null. */
export const currentBundlePath = (doc: Document = document): string | null => {
  for (const s of Array.from(doc.querySelectorAll<HTMLScriptElement>('script[src]'))) {
    const m = s.getAttribute('src')?.match(BUNDLE_RE);
    if (m) return m[0];
  }
  return null;
};

/** 서버 index.html 의 진입 번들 경로. 못 읽으면 null(판단 보류). */
export const fetchLatestBundlePath = async (fetchImpl: typeof fetch = fetch): Promise<string | null> => {
  try {
    const res = await fetchImpl(`/?_v=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return null;
    return (await res.text()).match(BUNDLE_RE)?.[0] ?? null;
  } catch {
    return null;
  }
};

/** 새 버전이 배포됐는가 — 5분마다, 그리고 탭으로 돌아올 때 확인한다. */
export const useNewVersionAvailable = (): boolean => {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    const current = currentBundlePath();
    if (!current) return undefined;
    let stopped = false;
    const check = async () => {
      const latest = await fetchLatestBundlePath();
      if (!stopped && latest && latest !== current) setAvailable(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  return available;
};
